import { Component, Element, h, Host, Listen, Prop, State, Watch } from "@stencil/core";
import { logger } from "../../utils/console";
import { limitSlotElements } from "../../utils/limitSlotElements";
import { WidthChangeEvent } from "./interfaces/width-change";

/**
 * UxTableNeo - Experimental table component
 * @slot footer - The footer of the table, limited to ux-table-footer-neo
 * @slot - The rows of the tablem limited to <ux-table-head> and <ux-table-body>
 * @slot caption - The caption of the table, limited to ux-table-caption-neo
 */
@Component({
    tag: "ux-table-neo",
    styleUrl: "table-neo.scss",
    shadow: true
})
export class TableNEo {
    @Element() host!: HTMLUxTableNeoElement;

    /**
     * The summary of the table
     */
    @Prop({ reflect: true }) readonly summary!: string;

    /**
     * Set table to loading state. All child rows & cells will be loading.
     */
    @Prop({ reflect: true }) readonly loading: boolean;

    @Listen("rowCountChange") rowCountChangeHandler(event: CustomEvent<number>) {
        this.rowCount = event.detail;
        this.host.style.setProperty("--ux-row-count", this.rowCount.toString());
        event.stopPropagation();
    }
    @State() rowCount: number;

    @Listen("headCellCountChange") headCellCountChange(event: CustomEvent<number>) {
        this.headCellCount = event.detail;
        this.host.style.setProperty("--ux-head-cell-count", this.headCellCount.toString());
        event.stopPropagation();
    }
    @State() headCellCount: number;
    @State() customWidths: string[] = [];

    @Listen("widthChange") widthChangeHandler(event: CustomEvent<WidthChangeEvent>) {
        this.customWidths = [];
        for (let index = 0; index < this.headCellCount; index++) {
            if (event.detail.colIndex === index) {
                this.customWidths.push(`${event.detail.width}px`);
            } else {
                this.customWidths.push("1fr");
            }
        }
        this.host.style.setProperty("--ux-table-col-template", this.customWidths.join(" "));
    }

    componentWillRender() {
        if (!this.summary) {
            logger.warn("No summary provided for table");
        }
    }

    componentDidLoad() {
        limitSlotElements(this.host, {
            allowed: {
                caption: ["ux-table-caption-neo"],
                footer: ["ux-table-footer-neo"]
            }
        });
    }

    @Watch("loading")
    setLoadingState(enabled: boolean = this.loading) {
        const rowsHead = Array.from(this.host.querySelectorAll<HTMLUxTableRowNeoElement>("ux-table-header-neo ux-table-row-neo"));
        const rows = Array.from(this.host.querySelectorAll<HTMLUxTableRowNeoElement>("ux-table-body-neo ux-table-row-neo"));
        for (const row of [...rowsHead, ...rows]) {
            row.loading = enabled;
        }
    }

    render() {
        return (
            <Host role="table" aria-rowcount={this.rowCount}>
                <slot name="caption" />
                <slot />
                <slot name="footer" />
            </Host>
        );
    }
}
